import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { useErrorHandler } from '../services/errorHandler';
import { useTranslation } from 'react-i18next';

interface CartItem {
  productId: number;
  cartItemId: number;
  quantity: number;
  colorId?: number;
}

interface CartContextType {
  cartItems: Map<number, CartItem>; // productId -> CartItem
  isInCart: (productId: number) => boolean;
  getCartItemId: (productId: number) => number | null;
  getCartItemQuantity: (productId: number) => number;
  addToCart: (productId: number, quantity: number, colorId?: number) => Promise<boolean>;
  removeFromCart: (cartItemId: number) => Promise<boolean>;
  updateQuantity: (cartItemId: number, quantity: number) => Promise<boolean>;
  loadCart: () => Promise<void>;
  isLoading: boolean;
  totalItems: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<Map<number, CartItem>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const { isAuthenticated, setCartCount } = useAuth();
  const errorHandler = useErrorHandler();
  const { t } = useTranslation();

  // Load cart from backend
  const loadCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCartItems(new Map());
      setTotalItems(0);
      setCartCount(0);
      return;
    }

    try {
      const { data } = await api.get('/cart');
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      
      const cartMap = new Map<number, CartItem>();
      let total = 0;
      
      items.forEach((item: any) => {
        const productId = Number(item.ProductId || item.productId || item.Product?.Id || item.product?.Id || item.Product?.id || item.product?.id);
        const cartItemId = Number(item.Id || item.id);
        const quantity = Number(item.Quantity || item.quantity || 1);
        const colorId = item.ProductColorId || item.productColorId;
        
        if (productId && cartItemId) {
          cartMap.set(productId, { productId, cartItemId, quantity, colorId });
          total += quantity;
        }
      });
      
      setCartItems(cartMap);
      setTotalItems(total);
      setCartCount(total);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCartItems(new Map());
      setTotalItems(0);
      setCartCount(0);
    }
  }, [isAuthenticated, setCartCount]);

  // Load cart on mount and when auth changes
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Check if product is in cart
  const isInCart = useCallback((productId: number) => {
    return cartItems.has(productId);
  }, [cartItems]);

  // Get cart item ID
  const getCartItemId = useCallback((productId: number) => {
    const item = cartItems.get(productId);
    return item ? item.cartItemId : null;
  }, [cartItems]);

  // Get cart item quantity
  const getCartItemQuantity = useCallback((productId: number) => {
    const item = cartItems.get(productId);
    return item ? item.quantity : 0;
  }, [cartItems]);

  // Add to cart
  const addToCart = useCallback(async (productId: number, quantity: number = 1, colorId?: number): Promise<boolean> => {
    if (!isAuthenticated) {
      errorHandler.showWarning(t('auth.loginRequired', 'You must be logged in to perform this action'));
      return false;
    }

    setIsLoading(true);

    try {
      const { data } = await api.post('/cart', {
        productId,
        quantity,
        productColorId: colorId,
      });

      // Reload cart to get accurate data
      await loadCart();
      
      // Use translation key for localized message
      errorHandler.showSuccess(t('cart.itemAdded', 'Product added to cart'));
      return true;
    } catch (error) {
      errorHandler.handleError(error, { showToast: true });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, loadCart, errorHandler, t]);

  // Remove from cart
  const removeFromCart = useCallback(async (cartItemId: number): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }

    // Optimistic update
    const productIdToRemove = Array.from(cartItems.entries()).find(([_, item]) => item.cartItemId === cartItemId)?.[0];
    
    if (productIdToRemove) {
      const removedItem = cartItems.get(productIdToRemove);
      setCartItems(prev => {
        const next = new Map(prev);
        next.delete(productIdToRemove);
        return next;
      });
      
      if (removedItem) {
        const newTotal = Math.max(0, totalItems - removedItem.quantity);
        setTotalItems(newTotal);
        setCartCount(newTotal);
      }
    }

    setIsLoading(true);

    try {
      await api.delete(`/cart/${cartItemId}`);
      errorHandler.showSuccess(t('cart.itemRemoved', 'Item removed from cart'));
      return true;
    } catch (error) {
      // Revert optimistic update on error
      await loadCart();
      errorHandler.handleError(error, { showToast: true });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, cartItems, totalItems, loadCart, setCartCount, errorHandler, t]);

  // Update quantity
  const updateQuantity = useCallback(async (cartItemId: number, quantity: number): Promise<boolean> => {
    if (!isAuthenticated || quantity < 1) {
      return false;
    }

    setIsLoading(true);

    try {
      await api.put(`/cart/${cartItemId}`, { quantity });
      
      // Reload cart to get accurate data
      await loadCart();
      
      return true;
    } catch (error) {
      errorHandler.handleError(error, { showToast: true });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, loadCart, errorHandler]);

  const value: CartContextType = {
    cartItems,
    isInCart,
    getCartItemId,
    getCartItemQuantity,
    addToCart,
    removeFromCart,
    updateQuantity,
    loadCart,
    isLoading,
    totalItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within CartProvider');
  }
  return context;
};

